import {
  hasCsdAnalyticsConsent,
  hasCsdMarketingConsent,
  subscribeCsdConsent,
} from './privacyConsent';

type AnalyticsPayload = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

const appendScript = (id: string, src: string) => {
  if (typeof document === 'undefined' || document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const getSessionId = () => {
  try {
    const existing = window.sessionStorage.getItem('csd:session-id');
    if (existing) return existing;

    const sessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `csd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem('csd:session-id', sessionId);
    return sessionId;
  } catch {
    return '';
  }
};

const sendFirstPartyEvent = (eventName: string, payload: AnalyticsPayload) => {
  if (
    typeof window === 'undefined' ||
    !hasCsdAnalyticsConsent() ||
    window.location.pathname.startsWith('/admin')
  ) {
    return;
  }

  fetch('/api/analytics/event', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSD-Consent': 'analytics-v1',
    },
    body: JSON.stringify({
      eventName,
      payload,
      path: window.location.pathname,
      pageTitle: '',
      referrer: '',
      sessionId: getSessionId(),
    }),
    keepalive: true,
  }).catch(() => {
    // Analytics must never break conversion paths.
  });
};

export const initAnalytics = () => {
  if (!hasCsdAnalyticsConsent()) return;
  const gtmId = import.meta.env.VITE_GTM_ID;
  const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const metaPixelId = import.meta.env.VITE_META_PIXEL_ID;

  if (gtmId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    appendScript('csd-gtm', `https://www.googletagmanager.com/gtm.js?id=${gtmId}`);
  }

  if (gaMeasurementId) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtagFallback(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', gaMeasurementId, { send_page_view: false });
    appendScript('csd-ga4', `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`);
  }

  if (metaPixelId && hasCsdMarketingConsent() && !window.fbq) {
    window.fbq = function fbqFallback(...args: unknown[]) {
      (window.fbq as any).queue = (window.fbq as any).queue || [];
      (window.fbq as any).queue.push(args);
    };
    (window.fbq as any).loaded = true;
    (window.fbq as any).version = '2.0';
    window.fbq('init', metaPixelId);
    appendScript('csd-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
  }
};

export const trackEvent = (eventName: string, payload: AnalyticsPayload = {}) => {
  if (!hasCsdAnalyticsConsent()) return;
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );

  window.dataLayer?.push({
    event: eventName,
    ...cleanPayload,
  });

  window.gtag?.('event', eventName, cleanPayload);

  sendFirstPartyEvent(eventName, cleanPayload);
};

export const trackPageView = (path: string, title = document.title) => {
  if (!hasCsdAnalyticsConsent()) return;
  const cleanPath = path.split('?')[0].split('#')[0];
  const pageLocation = `${window.location.origin}${cleanPath}`;
  window.dataLayer?.push({
    event: 'page_view',
    page_path: cleanPath,
    page_location: pageLocation,
    page_title: title,
  });

  window.gtag?.('event', 'page_view', {
    page_path: cleanPath,
    page_location: pageLocation,
    page_title: title,
  });

  if (hasCsdMarketingConsent()) window.fbq?.('track', 'PageView');

  sendFirstPartyEvent('page_view', {
    page_path: cleanPath,
    page_location: pageLocation,
    page_title: title,
  });
};

subscribeCsdConsent((consent) => {
  if (consent.analytics) {
    initAnalytics();
    trackPageView(window.location.pathname);
    return;
  }
  ['csd-gtm', 'csd-ga4', 'csd-meta-pixel'].forEach((id) => document.getElementById(id)?.remove());
  window.dataLayer = [];
  window.gtag = undefined;
  window.fbq = undefined;
});
